import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { EditableText } from '@/hooks/useEditableContent';
import { Brain, Heart, Moon, Sparkles, Activity, Shield, Loader2, Eye, Star, BookOpen } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { SEO } from '@/components/SEO';
import therappiLogo from '@/assets/therappi-logo.svg';
import therappiWhoopLogo from '@/assets/therappi-whoop-combined.svg';
import therappiTextLogo from '@/assets/therappi-text-logo.svg';
import phoneCheckinMockup from '@/assets/phone-checkin-dynamic.png';
import phoneTherapyMockup from '@/assets/phone-therapy-dynamic.png';
import phoneFoodMockup from '@/assets/phone-food-dynamic.png';
import phoneBiodataMockup from '@/assets/phone-biodata-dynamic.png';

const Beta = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [whoopEmail, setWhoopEmail] = useState('');
  const [isWhoopSubmitting, setIsWhoopSubmitting] = useState(false);

  const handleWaitlistSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !name) {
      toast.error('Please fill in all fields');
      return;
    }
    setIsSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke('waitlist-signup', {
        body: { name, email }
      });
      if (error) {
        console.error('Function invocation error:', error);
        if (error.message?.includes('already on the waitlist')) {
          toast.error('This email is already on our waitlist!');
        } else {
          toast.error(error.message || 'Something went wrong. Please try again.');
        }
        return;
      }
      if (data?.error) {
        if (data.error.includes('already on the waitlist')) {
          toast.error('This email is already on our waitlist!');
        } else {
          toast.error(data.error);
        }
      } else {
        toast.success('Thank you for joining our waitlist! Check your email for confirmation.');
        setName('');
        setEmail('');
      }
    } catch (error: any) {
      console.error('Waitlist signup error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhoopPioneerSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!whoopEmail) {
      toast.error('Please provide your email');
      return;
    }
    setIsWhoopSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke('waitlist-signup', {
        body: {
          name: 'WHOOP Pioneer',
          email: whoopEmail,
          notes: 'WHOOP Pioneer Programme Participant'
        }
      });
      if (error) {
        console.error('Function invocation error:', error);
        if (error.message?.includes('already on the waitlist')) {
          toast.error('This email is already registered!');
        } else {
          toast.error(error.message || 'Something went wrong. Please try again.');
        }
        return;
      }
      if (data?.error) {
        if (data.error.includes('already on the waitlist')) {
          toast.error('This email is already registered!');
        } else {
          toast.error(data.error);
        }
      } else {
        toast.success('Welcome to the Pioneer Programme! Check your email for confirmation.');
        setWhoopEmail('');
      }
    } catch (error: any) {
      console.error('WHOOP signup error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsWhoopSubmitting(false);
    }
  };

  const scrollToWaitlist = () => {
    const waitlistSection = document.getElementById('waitlist-section');
    waitlistSection?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background/95 to-primary/5">
      <SEO
        title="Join the Therappi® Beta - Whole-Self Mental Wellness"
        description="Be among the first to experience Therappi®: QuantumSelf Psychology™, biometrics and AI reflection across every level of the self."
        path="/beta"
      />
      {/* Header */}
      <header className="border-b border-border/50 bg-background/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-start">
            <img src={therappiLogo} alt="Therappi<sup className='text-[0.6em]'>®</sup> Logo" className="h-24 sm:h-32 md:h-48 object-contain" />
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 md:py-10">
        <div className="max-w-5xl mx-auto text-center">
          {/* Coming Soon Banner */}
          <div className="mb-6 sm:mb-8">
            <div className="inline-flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-primary/20 via-accent/20 to-primary/20 rounded-full border border-primary/30 backdrop-blur-sm">
              <Sparkles className="h-4 w-4 sm:h-5 sm:w-5 text-primary animate-pulse" />
              <p className="text-base sm:text-lg font-semibold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                <EditableText contentKey="beta.hero.banner" defaultContent="Coming Soon..." category="beta" />
              </p>
            </div>
          </div>

          {/* Main Hero Logo */}
          <div className="mb-6 sm:mb-8 mt-4 sm:mt-8 flex justify-center px-4">
            <img src={therappiTextLogo} alt="Therappi<sup className='text-[0.6em]'>®</sup> - Where Science Meets Soul" className="w-full max-w-[180px] sm:max-w-xl md:max-w-2xl lg:max-w-3xl" />
          </div>

          {/* Hero Statement */}
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 leading-tight px-2">
            <EditableText contentKey="beta.hero.headline1" defaultContent="Your body already knows the answers." category="beta" />
            <br />
            <EditableText contentKey="beta.hero.headline2" defaultContent="Therappi<sup className='text-[0.6em]'>®</sup> helps you hear them." category="beta" className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent" />
          </h1>

          <p className="text-base sm:text-lg md:text-xl text-muted-foreground mb-6 sm:mb-8 leading-relaxed px-2 max-w-4xl mx-auto">
            <EditableText 
              contentKey="beta.hero.description1" 
              defaultContent="Tired of drowning in data without direction? Your wearable shows recovery scores, your period tracker logs dates, your meditation app counts minutes-but none of them talk to each other. None of them show you the invisible threads connecting your sleep to your mood, your cycle to your performance, your anxiety to your HRV." 
              category="beta" 
            />
          </p>

          <p className="text-base sm:text-lg md:text-xl mb-6 sm:mb-8 leading-relaxed px-2 max-w-4xl mx-auto font-medium">
              <EditableText 
              contentKey="beta.hero.description2" 
              defaultContent="Therappi<sup className='text-[0.6em]'>®</sup> is the first platform that doesn't just track your life-it understands it. Then it shows you exactly what to do about it." 
              category="beta" 
            />
          </p>

          {/* Dramatic Proof Point */}
          <div className="mb-10 sm:mb-12 px-4">
            <Card className="p-6 sm:p-8 bg-gradient-to-br from-primary/10 to-accent/10 border-primary/30">
              <p className="text-base sm:text-lg md:text-xl leading-relaxed">
                <EditableText 
                  contentKey="beta.hero.proof_point" 
                  defaultContent="Early pilot members report catching anxiety spirals 5-7 days before they hit, understanding why they feel exhausted on day 23 of their cycle, and finally seeing how that third glass of wine shows up in tomorrow's behaviour. The difference? Therappi<sup className='text-[0.6em]'>®</sup> connects the dots other apps cannot see." 
                  category="beta" 
                />
              </p>
              <p className="text-xs sm:text-sm text-muted-foreground mt-4">
                <EditableText 
                  contentKey="beta.hero.disclaimer" 
                  defaultContent="*Based on early pilot programme data with initial testers. Individual results may vary. Therappi<sup className='text-[0.6em]'>®</sup> provides wellbeing support and is not a substitute for professional medical or mental health care." 
                  category="beta" 
                />
              </p>
            </Card>
          </div>

          {/* WHOOP Integration Highlight */}
          <div className="flex flex-col items-center justify-center gap-3 sm:gap-4 mb-10 sm:mb-12">
            <div className="flex items-center justify-center">
              <img src={therappiWhoopLogo} alt="Therappi<sup className='text-[0.6em]'>®</sup> × WHOOP" className="h-16 sm:h-20 md:h-24 lg:h-32 object-contain" />
            </div>
            <p className="text-sm sm:text-base md:text-lg px-4 max-w-2xl">
              <EditableText 
                contentKey="beta.hero.whoop_tagline" 
                defaultContent="The first platform where your biometrics meet psychological wisdom and spiritual insight-showing that longevity isn't just about physical health, it's about caring for all dimensions of who you are" 
                category="beta" 
              />
            </p>
          </div>

        </div>
      </section>

      {/* WHOOP × Biometric Intelligence */}
      <section className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="max-w-6xl mx-auto">
          <div className="mb-12">
            <div className="grid md:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div>
                <div className="mb-6">
                  <Badge className="text-xs px-3 py-1.5 inline-block" variant="outline">
                    <Activity className="h-3 w-3 mr-2 inline-block" />
                    <span className="inline-block">
                      <EditableText contentKey="beta.whoop.badge" defaultContent="The only AI wellness companion that knows your body as well as your mind" category="beta" />
                    </span>
                  </Badge>
                </div>
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6">
                  <EditableText contentKey="beta.whoop.headline" defaultContent="Your Body Knows the Truth" category="beta" />
                </h2>
                <p className="text-lg text-muted-foreground mb-8">
                  <EditableText 
                    contentKey="beta.whoop.description" 
                    defaultContent="Connect your WHOOP or wearable to add another layer of truth to your self-care. When you say 'I am fine' but your HRV has dropped 40 points. When you claim rest helps but your recovery scores tell a different story. When emotional patterns show up in your sleep architecture before you are consciously aware. Your body does not lie-and now your reflection space integrates what your body knows. Mind-body connection is not philosophy anymore. It is measurable, trackable, and integrated into your reflection journey." 
                    category="beta" 
                  />
                </p>
              </div>
              <div className="flex justify-center">
                <img src={phoneBiodataMockup} alt="Therappi<sup className='text-[0.6em]'>®</sup> WHOOP Integration Interface" className="w-full max-w-sm rounded-3xl shadow-2xl" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Value Section */}
      <section className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6">
              <EditableText contentKey="beta.value.headline" defaultContent="This is more than a tracking app - it's Personalised Transformation." category="beta" />
            </h2>
            <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto">
              <EditableText 
                contentKey="beta.value.description" 
                defaultContent="Therappi<sup className='text-[0.6em]'>®</sup> weaves together your biometrics, hormonal rhythms, sleep architecture, emotional patterns, and daily behaviours into a living map of your system. Then - and this is where everything changes - it gives you personalised reflection prompts precisely when you need them, using the Soulprint Sequence™: Regulate, Recognise, Reflect, Release, Reprogramme, Reconnect." 
                category="beta" 
              />
            </p>
          </div>

          {/* AI Reflective Support Section */}
          <div className="mb-10">
            <div className="grid md:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div className="order-2 md:order-1 flex justify-center">
                <img src={phoneTherapyMockup} alt="Therappi<sup className='text-[0.6em]'>®</sup> AI Reflective Support Interface" className="w-full max-w-sm rounded-3xl shadow-2xl" />
              </div>
              
              <div className="order-1 md:order-2">
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6">
                  <EditableText contentKey="beta.therapy.headline" defaultContent="AI-Powered Therapeutic Support That Sees What You Cannot" category="beta" />
                </h2>
                <p className="text-xl mb-4 font-semibold">
                  <EditableText contentKey="beta.therapy.subheadline" defaultContent="Your Timeline Reveals Your Blindspots" category="beta" />
                </p>
                <p className="text-lg text-muted-foreground mb-6">
                  <EditableText 
                    contentKey="beta.therapy.description" 
                    defaultContent="We all have blindspots-patterns we repeat despite wanting to change, relationships we return to despite the pain, behaviours we choose not to see. Unlike AI chatbots that forget your history, Therappi<sup className='text-[0.6em]'>®</sup> builds your complete reflection timeline across weeks and months. It notices what you cannot: the third time you withdrew when someone got close, how you self-sabotage every time things go well, the pattern of choosing unavailable partners, the way you say you want boundaries but cancel them when tested." 
                    category="beta" 
                  />
                </p>
                <div className="space-y-4">
                  <div className="bg-background/50 p-4 rounded-lg border border-primary/20">
                    <div className="flex items-center gap-2 mb-2">
                      <Eye className="h-5 w-5 text-primary" />
                      <h3 className="font-semibold text-primary">
                        <EditableText contentKey="beta.therapy.feature1.title" defaultContent="Pattern Recognition Across Time" category="beta" />
                      </h3>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      <EditableText 
                        contentKey="beta.therapy.feature1.description" 
                        defaultContent="This is the fourth time you have blamed yourself in a conflict. Every promotion triggers the same anxiety spiral. You withdraw when people need you most. AI spots patterns across conversations that single sessions cannot reveal-gently reflecting what you have been choosing not to see." 
                        category="beta" 
                      />
                    </p>
                  </div>
                  <div className="bg-background/50 p-4 rounded-lg border border-primary/20">
                    <div className="flex items-center gap-2 mb-2">
                      <BookOpen className="h-5 w-5 text-primary" />
                      <h3 className="font-semibold text-primary">
                        <EditableText contentKey="beta.therapy.feature2.title" defaultContent="The Sanctuary: Your Complete History" category="beta" />
                      </h3>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      <EditableText 
                        contentKey="beta.therapy.feature2.description" 
                        defaultContent="Every conversation stored, searchable, analysable. Unlike therapy apps that reset with each session, The Sanctuary remembers everything-building a comprehensive reflection timeline that reveals recurring themes, tracks breakthrough moments, and deepens connection. Your history is your power." 
                        category="beta" 
                      />
                    </p>
                  </div>
                  <div className="bg-background/50 p-4 rounded-lg border border-primary/20">
                    <div className="flex items-center gap-2 mb-2">
                      <Brain className="h-5 w-5 text-primary" />
                      <h3 className="font-semibold text-primary">
                        <EditableText contentKey="beta.therapy.feature3.title" defaultContent="Multi-Modal Therapeutic Intelligence" category="beta" />
                      </h3>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      <EditableText 
                        contentKey="beta.therapy.feature3.description" 
                        defaultContent="Choose your pathway: Person-Centred, CBT, Psychodynamic-Inspired, DBT, ACT, or QuantumSelf Psychology™. Trained in clinical modalities, adapted to your needs, evolving with you-not generic prompts, but personalised reflective depth that remembers your journey." 
                        category="beta" 
                      />
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* The Sanctuary Section */}
          <div className="mb-10">
            <Card className="p-8 md:p-12 bg-gradient-to-br from-accent/10 via-primary/5 to-accent/10 border-accent/30">
              <div className="flex flex-col items-center justify-center mb-6">
                <div className="flex items-center mb-4">
                  <Shield className="h-12 w-12 text-primary mr-4" />
                  <h2 className="text-3xl sm:text-4xl font-bold">
                    <EditableText contentKey="beta.sanctuary.title" defaultContent="The Sanctuary" category="beta" />
                  </h2>
                </div>
                <Badge className="text-sm px-4 py-2" variant="secondary">
                  <BookOpen className="h-4 w-4 mr-2" />
                  <EditableText contentKey="beta.sanctuary.badge" defaultContent="Your Complete Therapeutic Timeline + Crisis Tools" category="beta" />
                </Badge>
              </div>
              <p className="text-lg text-center text-muted-foreground mb-8 max-w-3xl mx-auto">
                <EditableText 
                  contentKey="beta.sanctuary.description" 
                  defaultContent="Developed by a Chartered Counselling Psychologist with over 25 years of practice experience. The Sanctuary is where your complete reflection journey lives-every conversation archived, every pattern tracked, every blindspot illuminated. Plus instant access to evidence-based crisis tools for anxiety, depression, trauma, grief, and stress." 
                  category="beta" 
                />
              </p>
            </Card>
          </div>

          {/* Predictive Wellness Intelligence */}
          <div className="mb-10">
            <div className="grid md:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div>
                <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                  <EditableText contentKey="beta.predictive.headline" defaultContent="Predictive Wellness Intelligence" category="beta" />
                </h2>
                <p className="text-lg text-muted-foreground mb-6">
                  <EditableText 
                    contentKey="beta.predictive.description" 
                    defaultContent="Eight-dimensional daily tracking that identifies imbalance before it escalates - detecting early signs of fatigue, stress, and burnout through integrated data and emotional trends. Because early awareness is the new paradigm of self-care." 
                    category="beta" 
                  />
                </p>
              </div>
              <div className="flex justify-center">
                <img src={phoneCheckinMockup} alt="Therappi<sup className='text-[0.6em]'>®</sup> Daily Check-in Interface" className="w-full max-w-sm rounded-3xl shadow-2xl" />
              </div>
            </div>
          </div>

          {/* Food-Mood Mirror */}
          <div className="mb-10">
            <div className="grid md:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div className="order-2 md:order-1 flex justify-center">
                <img src={phoneFoodMockup} alt="Therappi<sup className='text-[0.6em]'>®</sup> Food-Mood Mirror Interface" className="w-full max-w-sm rounded-3xl shadow-2xl" />
              </div>
              <div className="order-1 md:order-2">
                <div className="flex items-center mb-6">
                  <Brain className="h-12 w-12 text-primary mr-4" />
                  <h2 className="text-3xl sm:text-4xl font-bold">
                    <EditableText contentKey="beta.food_mood.headline" defaultContent="The Food-Mood Mirror™" category="beta" />
                  </h2>
                </div>
                <p className="text-xl mb-4 font-semibold">
                  <EditableText contentKey="beta.food_mood.subheadline" defaultContent="Scan Your Meal. See Not Just Calories - But Consciousness." category="beta" />
                </p>
                <p className="text-lg text-muted-foreground mb-6">
                  <EditableText 
                    contentKey="beta.food_mood.description" 
                    defaultContent="Discover how your choices affect your mood, microbiome, energy, and emotional equilibrium. This isn't a diet function-it's a therapeutic awareness tool that reveals how food shapes your mental health, neurochemistry, and energetic frequency." 
                    category="beta" 
                  />
                </p>
              </div>
            </div>
          </div>

          {/* Evidence-Informed Features */}
          <div className="mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4">
              <EditableText contentKey="beta.features.headline" defaultContent="Tools that heal, not just measure" category="beta" />
            </h2>
            <p className="text-lg text-muted-foreground text-center mb-12 max-w-3xl mx-auto">
              <EditableText 
                contentKey="beta.features.description" 
                defaultContent="Therappi<sup className='text-[0.6em]'>®</sup> doesn't stop at showing you data. Every feature is designed for deep insight and immediate intervention-so you can transform, not just track." 
                category="beta" 
              />
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              <Card className="p-6 hover:shadow-lg transition-shadow">
                <Shield className="h-10 w-10 text-primary mb-4" />
                <h3 className="text-xl font-bold mb-3">
                  <EditableText contentKey="beta.features.sanctuary.title" defaultContent="The Sanctuary" category="beta" />
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  <EditableText 
                    contentKey="beta.features.sanctuary.description" 
                    defaultContent="AI-guided reflection with multiple approaches (CBT, Psychodynamic-Inspired, DBT, ACT)-not generic meditation tracks, but personalised reflective conversations. Crisis support with safety planning. Evidence-based skills library. What Headspace, Calm, and Wysa cannot offer: adaptive reflective intelligence that grows with you." 
                    category="beta" 
                  />
                </p>
                <Badge variant="secondary">Evidence-Informed</Badge>
              </Card>

              <Card className="p-6 hover:shadow-lg transition-shadow">
                <Heart className="h-10 w-10 text-primary mb-4" />
                <h3 className="text-xl font-bold mb-3">
                  <EditableText contentKey="beta.features.nervous_system.title" defaultContent="Nervous System Reset" category="beta" />
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  <EditableText 
                    contentKey="beta.features.nervous_system.description" 
                    defaultContent="Breathwork sequences, EFT tapping protocols, somatic release practices-all guided by real-time HRV feedback from your WHOOP. Shift your nervous system state in minutes, not hours. Move from dysregulation to calm, from freeze to flow, from reactive to responsive." 
                    category="beta" 
                  />
                </p>
                <Badge variant="secondary">Evidence-Based</Badge>
              </Card>

              <Card className="p-6 hover:shadow-lg transition-shadow">
                <BookOpen className="h-10 w-10 text-primary mb-4" />
                <h3 className="text-xl font-bold mb-3">
                  <EditableText contentKey="beta.features.journal.title" defaultContent="Quantum Journal" category="beta" />
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  <EditableText 
                    contentKey="beta.features.journal.description" 
                    defaultContent="Write to process, release, and integrate your experiences. Therappi<sup className='text-[0.6em]'>®</sup>'s AI analyses patterns across your entries-spotting themes, tracking emotional shifts, revealing insights you might miss. Your complete personal archive, searchable and meaningful, showing how far you've come." 
                    category="beta" 
                  />
                </p>
                <Badge variant="secondary">Therapeutic</Badge>
              </Card>

              <Card className="p-6 hover:shadow-lg transition-shadow">
                <Star className="h-10 w-10 text-primary mb-4" />
                <h3 className="text-xl font-bold mb-3">
                  <EditableText contentKey="beta.features.future_self.title" defaultContent="Future Self System" category="beta" />
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  <EditableText 
                    contentKey="beta.features.future_self.description" 
                    defaultContent="Map where you're going. Navigate the route with intention. Embody the frequency of your future self. Take aligned action daily. This isn't goal-setting-it's identity-based reflection inspired by neuroscience research, designed for sustainable transformation and lasting performance." 
                    category="beta" 
                  />
                </p>
                <Badge variant="secondary">Transformative</Badge>
              </Card>

              <Card className="p-6 hover:shadow-lg transition-shadow">
                <Sparkles className="h-10 w-10 text-primary mb-4" />
                <h3 className="text-xl font-bold mb-3">
                  <EditableText contentKey="beta.features.manifestation.title" defaultContent="Manifestation Magnifier" category="beta" />
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  <EditableText 
                    contentKey="beta.features.manifestation.description" 
                    defaultContent="Build personalised visualisation movies from your images, affirmations, and vision-then watch them daily. Rooted in neuroscience and future self research, this tool reprograms your subconscious mind to align behaviour with aspiration. Because manifestation without action is just wishing." 
                    category="beta" 
                  />
                </p>
                <Badge variant="secondary">Neuroscience-Based</Badge>
              </Card>

              <Card className="p-6 hover:shadow-lg transition-shadow">
                <Moon className="h-10 w-10 text-primary mb-4" />
                <h3 className="text-xl font-bold mb-3">
                  <EditableText contentKey="beta.features.sleep.title" defaultContent="Sleep & Dreams Suite" category="beta" />
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  <EditableText 
                    contentKey="beta.features.sleep.description" 
                    defaultContent="Analyse your sleep architecture with WHOOP integration. Journal dreams and receive AI-powered interpretation. Optimise rest with lunar cycle awareness. Access guided guided relaxation. Sleep is where your body heals and your subconscious speaks-Therappi<sup className='text-[0.6em]'>®</sup> helps you listen." 
                    category="beta" 
                  />
                </p>
                <Badge variant="secondary">Holistic</Badge>
              </Card>
            </div>
          </div>

          {/* Who is Therappi for? */}
          <div>
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4">
              <EditableText contentKey="beta.personas.headline" defaultContent="Who is Therappi<sup className='text-[0.6em]'>®</sup> for?" category="beta" />
            </h2>
            <p className="text-lg text-muted-foreground text-center mb-12 max-w-3xl mx-auto">
              <EditableText 
                contentKey="beta.personas.description" 
                defaultContent="Therappi<sup className='text-[0.6em]'>®</sup> meets you exactly where you are-whether you're managing anxiety, optimising performance, navigating hormonal transitions, deepening spiritual practice, or breaking sabotaging patterns. One platform. Every dimension of you." 
                category="beta" 
              />
            </p>
            
            <div className="space-y-8">
              {/* Persona 1 */}
              <Card className="p-8 bg-gradient-to-br from-primary/5 to-accent/5">
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0">
                    <div className="h-16 w-16 bg-gradient-primary rounded-full flex items-center justify-center">
                      <Moon className="h-8 w-8 text-primary-foreground" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-3">
                      <EditableText contentKey="beta.personas.persona1.title" defaultContent="For the one breaking old patterns" category="beta" />
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4 italic">
                      <EditableText 
                        contentKey="beta.personas.persona1.quote" 
                        defaultContent="I know what I should do-sleep better, drink less, eat well-but I keep falling back into the same cycles. Why can't I change? What's really driving these patterns?" 
                        category="beta" 
                      />
                    </p>
                  </div>
                </div>
              </Card>

              {/* Persona 2 */}
              <Card className="p-8 bg-gradient-to-br from-accent/5 to-primary/5">
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0">
                    <div className="h-16 w-16 bg-gradient-primary rounded-full flex items-center justify-center">
                      <Activity className="h-8 w-8 text-primary-foreground" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-3">
                      <EditableText contentKey="beta.personas.persona2.title" defaultContent="For the high performer chasing longevity" category="beta" />
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4 italic">
                      <EditableText 
                        contentKey="beta.personas.persona2.quote" 
                        defaultContent="My WHOOP tracks everything, but I'm still guessing. Why is my HRV tanking? How do I actually optimise recovery-not just physically, but mentally and emotionally too?" 
                        category="beta" 
                      />
                    </p>
                  </div>
                </div>
              </Card>

              {/* Persona 3 */}
              <Card className="p-8 bg-gradient-to-br from-primary/5 to-accent/5">
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0">
                    <div className="h-16 w-16 bg-gradient-primary rounded-full flex items-center justify-center">
                      <Brain className="h-8 w-8 text-primary-foreground" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-3">
                      <EditableText contentKey="beta.personas.persona3.title" defaultContent="For the anxious mind seeking peace" category="beta" />
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4 italic">
                      <EditableText 
                        contentKey="beta.personas.persona3.quote" 
                        defaultContent="Meditation apps give me 10 minutes of calm, then I'm back to spiralling. I need to understand why I feel this way-and actually prevent it before it starts." 
                        category="beta" 
                      />
                    </p>
                  </div>
                </div>
              </Card>

              {/* Persona 4 */}
              <Card className="p-8 bg-gradient-to-br from-accent/5 to-primary/5">
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0">
                    <div className="h-16 w-16 bg-gradient-primary rounded-full flex items-center justify-center">
                      <Sparkles className="h-8 w-8 text-primary-foreground" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-3">
                      <EditableText contentKey="beta.personas.persona4.title" defaultContent="For the soul craving alignment" category="beta" />
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4 italic">
                      <EditableText 
                        contentKey="beta.personas.persona4.quote" 
                        defaultContent="I journal, pull oracle cards, track the moon-but it all feels separate from my mental health work. Can manifestation and psychology actually work together? Can spiritual practice be grounded in real results?" 
                        category="beta" 
                      />
                    </p>
                  </div>
                </div>
              </Card>

              {/* Persona 5 */}
              <Card className="p-8 bg-gradient-to-br from-primary/5 to-accent/5">
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0">
                    <div className="h-16 w-16 bg-gradient-primary rounded-full flex items-center justify-center">
                      <Heart className="h-8 w-8 text-primary-foreground" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-3">
                      <EditableText contentKey="beta.personas.persona5.title" defaultContent="For the woman riding hormonal tides" category="beta" />
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4 italic">
                      <EditableText 
                        contentKey="beta.personas.persona5.quote" 
                        defaultContent="My mood crashes on day 24. My energy disappears in my luteal phase. Period trackers just log dates-but I need to understand why I feel this way and how to work with it, not against it." 
                        category="beta" 
                      />
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Beta Testing Sign-Up - MOVED TO BOTTOM */}
      <section id="waitlist-section" className="container mx-auto px-4 sm:px-6 py-10 sm:py-14 scroll-mt-20">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary/20 via-accent/20 to-primary/20 rounded-full border border-primary/30 backdrop-blur-sm mb-6">
              <Sparkles className="h-5 w-5 text-primary animate-pulse" />
              <span className="text-sm font-semibold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                <EditableText contentKey="beta.signup.badge" defaultContent="Limited Beta Access" category="beta" />
              </span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold mb-6 bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text">
              <EditableText 
                contentKey="beta.signup.headline" 
                defaultContent="Join the Exclusive Beta Testing Programme" 
                category="beta" 
              />
            </h2>
            <p className="text-lg sm:text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              <EditableText 
                contentKey="beta.signup.description" 
                defaultContent="Be part of an exclusive group shaping the future of holistic wellness. Beta testers get early access to groundbreaking features, personalised onboarding, and the opportunity to directly influence Therappi<sup className='text-[0.6em]'>®</sup>'s development. Spaces are limited-secure your spot today." 
                category="beta" 
              />
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* General Beta Testing */}
            <Card className="p-8 bg-gradient-to-br from-card to-card/50 backdrop-blur-sm border-primary/20">
              <h3 className="text-2xl font-bold mb-4">
                <EditableText 
                  contentKey="beta.signup.general.title" 
                  defaultContent="Apply for Beta Access" 
                  category="beta" 
                />
              </h3>
              <p className="text-muted-foreground mb-6">
                <EditableText 
                  contentKey="beta.signup.general.description" 
                  defaultContent="Get priority access when Therappi<sup className='text-[0.6em]'>®</sup> launches. Be notified first and skip the waitlist with exclusive beta pricing." 
                  category="beta" 
                />
              </p>
              <form onSubmit={handleWaitlistSubmit} className="space-y-4">
                <Input type="text" placeholder="Name:" value={name} onChange={e => setName(e.target.value)} required />
                <Input type="email" placeholder="Email:" value={email} onChange={e => setEmail(e.target.value)} required />
                <Button type="submit" className="w-full bg-gradient-to-r from-primary to-accent" size="lg" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <EditableText contentKey="beta.signup.general.button" defaultContent="Apply for Beta Access" category="beta" />
                  )}
                </Button>
              </form>
            </Card>

            {/* WHOOP Pioneer Programme */}
            <Card className="p-8 border-2 border-primary/40 bg-gradient-to-br from-primary/10 via-accent/10 to-primary/10 shadow-xl relative overflow-hidden">
              <div className="absolute top-4 right-4">
                <Badge className="bg-gradient-to-r from-primary to-accent text-primary-foreground border-0">
                  <EditableText contentKey="beta.signup.whoop.badge" defaultContent="VIP Access" category="beta" />
                </Badge>
              </div>
              <div className="flex justify-center mb-4">
                <img src={therappiWhoopLogo} alt="Therappi<sup className='text-[0.6em]'>®</sup> × WHOOP" className="h-16 object-contain" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-center bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                <EditableText 
                  contentKey="beta.signup.whoop.title" 
                  defaultContent="WHOOP Pioneer Programme" 
                  category="beta" 
                />
              </h3>
              <p className="text-muted-foreground mb-6 text-center">
                <EditableText 
                  contentKey="beta.signup.whoop.description" 
                  defaultContent="WHOOP users get exclusive early access to a first-of-its-kind integration of biometric tracking with AI reflective support. Shape the future of mind-body wellness with us." 
                  category="beta" 
                />
              </p>
              <form onSubmit={handleWhoopPioneerSubmit} className="space-y-4">
                <Input type="email" placeholder="Your email:" value={whoopEmail} onChange={e => setWhoopEmail(e.target.value)} required />
                <Button type="submit" className="w-full bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90 shadow-lg" size="lg" disabled={isWhoopSubmitting}>
                  {isWhoopSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Joining...
                    </>
                  ) : (
                    <EditableText contentKey="beta.signup.whoop.button" defaultContent="Join Pioneer Programme" category="beta" />
                  )}
                </Button>
              </form>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border/50 bg-background/80 backdrop-blur-sm py-8">
        <div className="container mx-auto px-4 sm:px-6 text-center text-muted-foreground">
          <p className="text-sm">
            <EditableText contentKey="beta.footer.copyright" defaultContent="© 2026 Therappi<sup className='text-[0.6em]'>®</sup>. Where Science Meets Soul - because healing the mind alone was never enough." category="beta" />
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Beta;
